import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { getIntercomIdentity } from '@/services/api.client';

/**
 * Intercom Messenger type declarations.
 *
 * The Messenger is loaded via a script tag and exposed as window.Intercom.
 * We declare a minimal interface here so TypeScript is happy without a
 * full @types/intercom-web dependency.
 */
declare global {
  interface Window {
    Intercom?: (command: string, options?: Record<string, unknown>) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

const APP_ID = import.meta.env['VITE_INTERCOM_APP_ID'] as string | undefined;

/**
 * Derives a human-readable role label from the user's primary property role.
 * Passed to Intercom as a custom attribute so Fin can tailor responses.
 */
function deriveRoleLabel(propertyRoles: Array<{ role: string }>, platformRole: string): string {
  if (platformRole === 'super_admin') return 'Super Admin';
  const role = propertyRoles[0]?.role;
  const labels: Record<string, string> = {
    property_manager: 'Property Manager',
    security_supervisor: 'Security Supervisor',
    guard: 'Guard',
    resident: 'Resident',
    enterprise_tenant_admin: 'Enterprise Admin',
    employee: 'Employee',
    org_admin: 'Organisation Admin',
  };
  return labels[role ?? ''] ?? 'User';
}

/**
 * useIntercom — boots the Intercom Messenger when a user is authenticated
 * and shuts it down on sign-out.
 *
 * - Loads the Intercom script once (idempotent).
 * - Fetches a signed JWT from the server to enable Messenger Security
 *   (prevents user impersonation — replaces the deprecated HMAC user_hash).
 * - Passes name, email, and role inside the JWT; the client-side boot call
 *   only carries the JWT and app_id.
 * - Calls `Intercom('shutdown')` when the user signs out to clear the session.
 * - Uses `Intercom('update')` for JWT refresh so Fin conversations are not
 *   interrupted by re-boots.
 *
 * Security notes:
 * - The Messenger API secret never reaches the browser — only the signed JWT.
 * - No PII is stored in localStorage; Intercom manages its own session cookie.
 * - If VITE_INTERCOM_APP_ID is not set, the hook is a no-op (safe in dev).
 */
export function useIntercom(): void {
  const { user, isAuthenticated } = useAuth();
  const scriptLoaded = useRef(false);
  const booted = useRef(false);

  // Fetch the signed JWT — only when authenticated and APP_ID is set.
  const { data: identity } = useQuery({
    queryKey: ['intercom-identity'],
    queryFn: getIntercomIdentity,
    enabled: isAuthenticated && !!APP_ID,
    // JWTs are valid for 5 minutes — refetch every 4 minutes to stay fresh.
    staleTime: 4 * 60_000,
    refetchInterval: 4 * 60_000,
    retry: 2,
    retryDelay: 1000,
  });

  // Load the Intercom script once.
  useEffect(() => {
    if (!APP_ID || scriptLoaded.current) return;
    if (typeof window === 'undefined') return;

    // Standard Intercom bootstrap snippet — installs window.Intercom as a queue
    // function so that boot/update calls made before the widget script finishes
    // loading still get processed once it does.
    // Adapted from https://developers.intercom.com/installing-intercom/web/installation
    const w = window as Window & {
      Intercom?: ((...args: unknown[]) => void) & { q?: unknown[]; c?: (args: unknown[]) => void };
      attachEvent?: (event: string, handler: () => void) => void;
    };

    const ic = w.Intercom;
    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const i: ((...args: unknown[]) => void) & { q?: unknown[]; c?: (args: unknown[]) => void } =
        function (...args: unknown[]) {
          (i.q = i.q ?? []).push(args);
        };
      i.q = [];
      i.c = (args) => {
        i.q?.push(args);
      };
      w.Intercom = i;

      const loadScript = () => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${APP_ID}`;
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
      };

      // In SPAs, `load` has almost always fired by the time React mounts.
      // Check readyState to avoid waiting for an event that already happened.
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        loadScript();
      } else if (w.attachEvent) {
        w.attachEvent('onload', loadScript);
      } else {
        window.addEventListener('load', loadScript, false);
      }
    }

    scriptLoaded.current = true;
  }, []);

  // Boot or update the Messenger when auth state or identity changes.
  useEffect(() => {
    if (!APP_ID || !window.Intercom) return;

    if (!isAuthenticated || !user) {
      // User signed out — shut down the Messenger to clear the session cookie.
      if (booted.current) {
        window.Intercom('shutdown');
        booted.current = false;
      }
      return;
    }

    const payload = {
      api_base: 'https://api-iam.intercom.io',
      app_id: APP_ID,
      // Custom attribute for Fin context — not sensitive, not in the JWT.
      role: deriveRoleLabel(user.propertyRoles, user.platformRole),
      ...(identity?.userJwt ? { intercom_user_jwt: identity.userJwt } : {}),
    };

    if (!booted.current) {
      // First call — boot the Messenger. This creates the widget and
      // starts the Fin session.
      window.Intercom('boot', payload);
      booted.current = true;
    } else {
      // Already booted — use 'update' to refresh the JWT without
      // tearing down the active Fin conversation.
      window.Intercom('update', payload);
    }
  }, [isAuthenticated, user, identity]);
}
